import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { 
  ArrowLeft,
  RefreshCw,
  AlertCircle,
  Trash2,
  User,
  CreditCard,
  Tv,
  MapPin,
  Wifi,
  Calendar,
  MessageSquare,
  Shield,
  AlertTriangle,
  Check,
  X,
  Clock,
  Search,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const AdminUserDetail = ({ userId, onBack }) => {
  const { apiRequest } = useAdminAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, mode: null });
  const [deleting, setDeleting] = useState(false);

  // Query history state
  const [queryHistory, setQueryHistory] = useState([]);
  const [queryStats, setQueryStats] = useState({
    total: 0,
    thisMonth: 0,
    byCategory: {},
    feedbackStats: { positive: 0, negative: 0, total: 0 }
  });
  const [showAllQueries, setShowAllQueries] = useState(false);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  const fetchUserData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/admin/user/${userId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }
      const data = await response.json();
      setUserData(data);

      // Also fetch query history
      await fetchQueryHistory();
    } catch (err) {
      console.error('Fetch user error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueryHistory = async () => {
    setLoadingQueries(true);
    try {
      // Get recent queries from chat_logs including feedback columns
      const { data: logs, error: logsError } = await supabase
        .from('chat_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (logsError) {
        console.log('Chat logs table may not exist yet:', logsError);
        setQueryHistory([]);
      } else {
        setQueryHistory(logs || []);

        // Calculate stats
        const now = new Date();
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const thisMonth = (logs || []).filter(l => new Date(l.created_at) >= monthAgo);

        const byCategory = {};
        (logs || []).forEach(log => {
          const cat = log.query_category || 'general';
          byCategory[cat] = (byCategory[cat] || 0) + 1;
        });

        // Calculate feedback stats
        const withFeedback = (logs || []).filter(l => l.feedback_rating);
        const positive = withFeedback.filter(l => l.feedback_rating === 'positive').length;
        const negative = withFeedback.filter(l => l.feedback_rating === 'negative').length;

        setQueryStats({
          total: logs?.length || 0,
          thisMonth: thisMonth.length,
          byCategory,
          feedbackStats: {
            positive,
            negative,
            total: withFeedback.length
          }
        });
      }
    } catch (err) {
      console.error('Error fetching query history:', err);
    } finally {
      setLoadingQueries(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  const handleDelete = async (mode) => {
    setDeleting(true);
    try {
      const response = await apiRequest('/api/admin/delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          mode,
          confirm: true
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Delete failed');
      }

      // If hard deleted, go back to list
      if (mode === 'hard') {
        onBack();
      } else {
        // Soft deleted - refresh the data
        fetchUserData();
      }
      setDeleteModal({ open: false, mode: null });
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const InfoCard = ({ icon: Icon, label, value, className = '' }) => (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="p-2 bg-gray-700 rounded-lg">
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <div>
        <p className="text-gray-400 text-xs">{label}</p>
        <p className="text-white">{value || '-'}</p>
      </div>
    </div>
  );

  const formatCategory = (cat) => {
    const labels = {
      where_to_watch: 'Where to Watch',
      optimization: 'Optimization',
      recommendation: 'Recommendations',
      live_sports: 'Live Sports',
      general: 'General',
    };
    return labels[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const colors = {
      where_to_watch: 'bg-blue-500/20 text-blue-400',
      optimization: 'bg-green-500/20 text-green-400',
      recommendation: 'bg-purple-500/20 text-purple-400',
      live_sports: 'bg-orange-500/20 text-orange-400',
      general: 'bg-gray-500/20 text-gray-400',
    };
    return colors[cat] || 'bg-gray-500/20 text-gray-400';
  };

  const formatFeedbackCategory = (cat) => {
    const labels = {
      incorrect: 'Incorrect info',
      didnt_answer: "Didn't answer",
      too_slow: 'Too slow',
      confusing: 'Confusing',
      accurate: 'Accurate',
      helpful: 'Helpful',
      fast: 'Fast',
      clear: 'Clear',
      other: 'Other',
    };
    return labels[cat] || cat;
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  // Feedback badge component
  const FeedbackBadge = ({ rating, category, text, logId }) => {
    if (!rating) return null;

    const isPositive = rating === 'positive';
    const isExpanded = expandedFeedback === logId;

    return (
      <div className="inline-flex flex-col">
        <button
          onClick={() => text && setExpandedFeedback(isExpanded ? null : logId)}
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${
            isPositive 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-red-500/20 text-red-400'
          } ${text ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
        >
          {isPositive ? (
            <ThumbsUp className="w-3 h-3" />
          ) : (
            <ThumbsDown className="w-3 h-3" />
          )}
          <span>{isPositive ? 'Helpful' : 'Not helpful'}</span>
          {category && (
            <span className="opacity-75">• {formatFeedbackCategory(category)}</span>
          )}
          {text && (
            <MessageSquare className="w-3 h-3 ml-1" />
          )}
        </button>
        {isExpanded && text && (
          <div className="mt-2 p-2 bg-gray-700 rounded text-xs text-gray-300 italic max-w-md">
            "{text}"
          </div>
        )}
      </div>
    );
  };

  // Feedback stats summary component
  const FeedbackStatsSummary = () => {
    const { positive, negative, total } = queryStats.feedbackStats;
    
    if (total === 0) return null;

    const positivePercent = Math.round((positive / total) * 100);

    return (
      <div className="flex items-center gap-4 px-6 py-3 bg-gray-700/50 border-b border-gray-700">
        <div className="text-sm text-gray-400">
          Feedback: <span className="text-white font-medium">{total}</span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <ThumbsUp className="w-4 h-4 text-green-500" />
          <span className="text-green-400">{positive}</span>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <ThumbsDown className="w-4 h-4 text-red-500" />
          <span className="text-red-400">{negative}</span>
        </div>
        <div className="text-sm text-gray-400">
          Satisfaction: <span className={`font-medium ${
            positivePercent >= 70 ? 'text-green-400' : 
            positivePercent >= 50 ? 'text-yellow-400' : 
            'text-red-400'
          }`}>{positivePercent}%</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Users
        </button>
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">Failed to load user</p>
            <p className="text-red-400/70 text-sm">{error}</p>
          </div>
          <button
            onClick={fetchUserData}
            className="ml-auto px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const user = userData?.user;
  const isDeleted = user?.deleted_at != null;

  // Get display name
  const displayName = user?.first_name 
    ? `${user.first_name} ${user.last_name || ''}`.trim()
    : user?.full_name || '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Users
        </button>
        <button
          onClick={fetchUserData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Deleted Banner */}
      {isDeleted && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">This user has been soft-deleted</p>
            <p className="text-red-400/70 text-sm">
              Deleted on {new Date(user.deleted_at).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* User Profile Card */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {user?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                {displayName && (
                  <h1 className="text-xl font-bold text-white">{displayName}</h1>
                )}
                <p className={`${displayName ? 'text-gray-400' : 'text-xl font-bold text-white'}`}>
                  {user?.email}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user?.plan_type === 'premium'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {user?.plan_type}
                  </span>
                  {user?.onboarding_completed ? (
                    <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                      <Check className="w-3 h-3" />
                      Onboarded
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                      Onboarding Incomplete
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Delete Buttons */}
            {!isDeleted ? (
              <button
                onClick={() => setDeleteModal({ open: true, mode: 'soft' })}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete User
              </button>
            ) : (
              <button
                onClick={() => setDeleteModal({ open: true, mode: 'hard' })}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Permanently Delete
              </button>
            )}
          </div>
        </div>

        {/* User Info Grid */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <InfoCard icon={User} label="User ID" value={user?.id?.slice(0, 8) + '...'} />
          <InfoCard icon={MapPin} label="Zip Code" value={user?.zip_code} />
          <InfoCard icon={Wifi} label="Internet Provider" value={user?.internet_provider} />
          <InfoCard icon={Calendar} label="Joined" value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : '-'} />
          <InfoCard icon={Clock} label="Last Active" value={user?.last_query_at ? formatTimeAgo(user.last_query_at) : 'Never'} />
          <InfoCard icon={MessageSquare} label="Total Queries" value={`${user?.queries_total || 0} (${user?.queries_used_month || 0} this month)`} />
        </div>

        {/* Eligibility */}
        <div className="px-6 pb-6">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Eligibility</h3>
          <div className="flex flex-wrap gap-2">
            {user?.is_student && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                Student
              </span>
            )}
            {user?.is_senior && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                Senior
              </span>
            )}
            {user?.is_veteran && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                Veteran
              </span>
            )}
            {user?.is_first_responder && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400">
                First Responder
              </span>
            )}
            {!user?.is_student && !user?.is_senior && !user?.is_veteran && !user?.is_first_responder && (
              <span className="text-gray-500 text-sm">None selected</span>
            )}
          </div>
        </div>
      </div>

      {/* Query History */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-400" />
            Query History
          </h2>
          <div className="flex items-center gap-4">
            {/* Category breakdown */}
            <div className="hidden md:flex items-center gap-2">
              {Object.entries(queryStats.byCategory).slice(0, 3).map(([cat, count]) => (
                <span key={cat} className={`px-2 py-1 rounded text-xs ${getCategoryColor(cat)}`}>
                  {formatCategory(cat)}: {count}
                </span>
              ))}
            </div>
            <span className="text-gray-400 text-sm">
              {queryStats.total} total
            </span>
          </div>
        </div>

        {/* Feedback Stats Summary */}
        <FeedbackStatsSummary />

        {loadingQueries ? (
          <div className="p-6 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-purple-500 animate-spin" />
          </div>
        ) : queryHistory.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            No queries yet
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-700">
              {(showAllQueries ? queryHistory : queryHistory.slice(0, 5)).map((log) => (
                <div key={log.id} className="px-6 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs ${getCategoryColor(log.query_category)}`}>
                          {formatCategory(log.query_category)}
                        </span>
                        {log.had_web_search && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400">
                            <Search className="w-3 h-3" />
                            Web Search
                          </span>
                        )}
                        {log.response_time_ms && (
                          <span className="text-xs text-gray-500">
                            {log.response_time_ms}ms
                          </span>
                        )}
                        {/* Feedback Badge */}
                        {log.feedback_rating && (
                          <FeedbackBadge 
                            rating={log.feedback_rating}
                            category={log.feedback_category}
                            text={log.feedback_text}
                            logId={log.id}
                          />
                        )}
                      </div>
                      <p className="text-white">{log.query}</p>
                      {log.response_summary && (
                        <p className="text-gray-500 text-sm truncate mt-1">
                          → {log.response_summary}
                        </p>
                      )}
                    </div>
                    <span className="text-gray-500 text-sm whitespace-nowrap">
                      {formatTimeAgo(log.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {queryHistory.length > 5 && (
              <div className="px-6 py-3 border-t border-gray-700">
                <button
                  onClick={() => setShowAllQueries(!showAllQueries)}
                  className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition-colors"
                >
                  {showAllQueries ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show all {queryHistory.length} queries
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Subscriptions */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Tv className="w-5 h-5 text-purple-400" />
            Subscriptions
          </h2>
          <span className="text-gray-400 text-sm">
            {userData?.subscriptions?.length || 0} services
          </span>
        </div>
        {userData?.subscriptions?.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            No subscriptions
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {userData?.subscriptions?.map((sub) => (
              <div key={sub.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{sub.service_name}</p>
                  {sub.subscription_tier && (
                    <p className="text-gray-400 text-sm">{sub.subscription_tier}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-white">${sub.monthly_cost?.toFixed(2) || '0.00'}/mo</p>
                  {sub.payment_method && (
                    <p className="text-gray-400 text-sm">{sub.payment_method}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Credit Cards */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            Credit Cards
          </h2>
          <span className="text-gray-400 text-sm">
            {userData?.creditCards?.length || 0} cards
          </span>
        </div>
        {userData?.creditCards?.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            No credit cards
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {userData?.creditCards?.map((card) => (
              <div key={card.id} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{card.card_name}</p>
                  {card.issuer && (
                    <p className="text-gray-400 text-sm">{card.issuer}</p>
                  )}
                </div>
                <div className="text-right">
                  {card.streaming_credit_monthly > 0 && (
                    <p className="text-green-400">${card.streaming_credit_monthly?.toFixed(2)}/mo credit</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-full ${
                deleteModal.mode === 'hard' 
                  ? 'bg-red-500/20' 
                  : 'bg-yellow-500/20'
              }`}>
                <AlertTriangle className={`w-6 h-6 ${
                  deleteModal.mode === 'hard' 
                    ? 'text-red-400' 
                    : 'text-yellow-400'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {deleteModal.mode === 'hard' ? 'Permanently Delete User' : 'Delete User'}
                </h3>
                <p className="text-gray-400 text-sm">{user?.email}</p>
              </div>
            </div>

            <p className="text-gray-300 mb-6">
              {deleteModal.mode === 'hard' ? (
                <>
                  This will <span className="text-red-400 font-medium">permanently delete</span> all user data including subscriptions, credit cards, and chat history. This action cannot be undone.
                </>
              ) : (
                <>
                  This will soft-delete the user. They will no longer be able to log in, but their data will be retained for 30 days before permanent deletion.
                </>
              )}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal({ open: false, mode: null })}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal.mode)}
                disabled={deleting}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  deleteModal.mode === 'hard'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                }`}
              >
                {deleting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    {deleteModal.mode === 'hard' ? 'Permanently Delete' : 'Delete'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetail;